import { PureCampaignBuilderProps } from "@/ui/NodeCanvas/types";
import { useCallback, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import ReactFlow, {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Controls,
  EdgeTypes,
  MarkerType,
  MiniMap,
  NodeTypes,
  Position,
  ReactFlowProvider,
} from "reactflow";
import { CustomEdge } from "./Edge/CustomEdge";
import { useContextMenu } from "react-contexify";
import { ContextMenu } from "@/ui/Menu/Menu";
import Node from "./Node/Node";

let id = 0;
const getId = () => `dndnode_${id++}`;

export const MENU_ID = "menu-id";

export const PureCampaignBuilder = forwardRef<{ fitView: () => void; fitViewWidth: () => void }, PureCampaignBuilderProps>(({
  readOnly = false,
  showMiniMap = true,
  showControls = true,
  panOnScroll = false,
  CustomNode = Node,

  direction = "horizontal",
  type = "funnel",

  objects = [],

  // from making the builder pure, we need to pass in the functions to handle the database calls
  deleteConnection = () => {},
  updateObject = () => {},
  deleteObject = () => {},
  createConnection = () => {},
  createObject = () => {},

  // event handlers
  onNodeDoubleClick = () => {},
  onChange = () => {},

  // get the dropdown items for the node
  getNodeDropdownItems = () => [],
}, ref) => {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [mostRecentNodeChange, setMostRecentNodeChange] = useState([]);
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [currentEventObject, setCurrentEventObject] = useState(null);

  // Define refs
  const reactFlowWrapper = useRef(null);

  // Define hooks
  const { show, hideAll } = useContextMenu({ id: MENU_ID });

  // Define memoized variables
  const nodeTypes: NodeTypes = useMemo(() => ({ defaultNode: CustomNode }), []);
  // const verticalNodeTypes: NodeTypes = useMemo(() => ({ defaultNode: Node }), []);
  const edgeTypes: EdgeTypes = useMemo(() => ({ custom: CustomEdge }), []);

  // Define callback functions

  // This function is called when the nodes on the graph are changed
  // It updates the nodes state with the new changes
  const onNodesChange = useCallback(
    (changes) => {
      setNodes((nds) => {
        setMostRecentNodeChange(changes);
        return applyNodeChanges(changes, nds);
      });
      onChange({ changes, nodes });
    },
    [setNodes]
  );

  // This function is called when the edges on the graph are changed
  // It updates the edges state with the new changes
  const onEdgesChange = useCallback(
    (changes) => {
      setEdges((eds) => {
        return applyEdgeChanges(changes, eds);
      });
      console.log(changes);
    },
    [setEdges]
  );

  // This function is called when edges are deleted from the graph
  // It loops through the array of edges to call GraphQL to remove all object connections
  const onEdgesDelete = (toDelete) => {
    console.log("onEdgesDelete", toDelete);

    for (let edge of toDelete) {
      const { source, target } = edge;
      deleteConnection({ source, target });
    }
  };

  // This function is called when a node is dragged and dropped
  // It updates the node in the database with the new position
  const onNodeDragStop = async (evt, node) => {
    console.log({ mostRecentNodeChange });
    for (let change of mostRecentNodeChange) {
      const { id, position } = change;
      if (position) {
        const { x, y } = position;

        await updateObject(id, {
          x: direction === "vertical" ? parseInt(y) : parseInt(x),
          y: direction === "vertical" ? parseInt(x) : parseInt(y),
        });
      }
    }
  };

  // This function is called when nodes are deleted from the graph
  // It loops through the array of nodes to call GraphQL to remove all provided nodes
  const onNodesDelete = async (toDelete) => {
    console.log("onNodesDelete", toDelete);

    for (let node of toDelete) {
      const { id } = node;
      await deleteObject(id);
    }
  };

  // This function is called when a new edge is created on the graph
  // It adds the new edge to the edges state and calls GraphQL to create a new connection
  const onConnect = async (params) => {
    const { source, target } = params;
    setEdges((eds) => addEdge(params, eds));
    await createConnection({ source, target });
  };

  // This function is called when a node is dragged over the graph
  // It prevents the default behavior of the event
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  // This function is called when a node is dropped onto the graph
  // It adds the new node to the nodes state and calls GraphQL to create a new object
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData("application/reactflow");

      if (typeof type === "undefined" || !type) return;

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode = {
        id: getId(),
        type,
        position,
        data: { label: `${type} node` },
      };

      setNodes((nds) => nds.concat(newNode));

      createObject({
        name: newNode.data.label,
        x:
          direction === "vertical"
            ? parseInt(newNode.position.y)
            : parseInt(newNode.position.x),
        y:
          direction === "vertical"
            ? parseInt(newNode.position.x)
            : parseInt(newNode.position.y),
        // campaignId: id,
      });
    },
    [reactFlowInstance]
  );

  // This function is called when a node is right-clicked
  // It opens the context menu and selects the node
  const onNodeContextMenu = (event, node) => {
    event.stopPropagation();
    setCurrentEventObject(event);

    show({ event });

    setActiveNodeId(node.id);
  };

  // This function is called when a node is clicked
  // It selects the node
  const onNodeClick = (event, node) => {
    event.stopPropagation();
    setActiveNodeId(node.id);
  };

  // This function is called when the canvas is right-clicked
  // It opens the context menu and deselects the node
  const onCanvasContextMenu = (event) => {
    console.log("onCanvasContextMenu", event);
  };

  // This function is called when the react flow instance is initialized
  // It sets the react flow instance state
  const onInit = (instance) => {
    setReactFlowInstance(instance);
  };

  const renderNodes = () => {
    // console.log("objects", objects);

    const newNodes = objects?.map((object: any) => {
      return {
        id: object.id,
        type: "defaultNode",
        position:
          direction === "vertical"
            ? { x: object.y, y: object.x }
            : { x: object.x, y: object.y },
        data: {
          showStats: false,
          stats: {},
          label: object.name,
          direction,
          viewType: type,
          campaignId: id,
          ...object,
        },
        sourcePosition:
          direction === "vertical" ? Position.Bottom : Position.Right,
        targetPosition: direction === "vertical" ? Position.Top : Position.Left,
      };
    });

    let newEdges = [];

    objects?.forEach(({ id, connectTo }: any) => {
      connectTo?.forEach((object: any) => {
        newEdges.push({
          id: `${id}-${object.id}`,
          source: id,
          target: object.id,
          markerEnd: {
            type: MarkerType.ArrowClosed,
          },
        });
      });
    });

    setEdges(newEdges);
    setNodes(newNodes);

    // console.log("Rendering Nodes", "Here are the Nodes I'll Render", newNodes);
    // console.log("newNodes", newNodes);
  };

  useEffect(() => {
    renderNodes();
  }, [objects]);

  useImperativeHandle(ref, () => ({
    fitView: () => {
      // console.log("will fitView", reactFlowInstance);
      setTimeout(() => {
        reactFlowInstance?.fitView();
      }, 200);
    },
    fitViewWidth: () => {
      // Fit view to width by calculating the bounding box and fitting horizontally
      setTimeout(() => {
        if (!reactFlowInstance || nodes.length === 0) return;
        
        // Calculate the bounding box of all nodes
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;

        console.log("nodes", nodes);
        
        nodes.forEach((node) => {
          const { x, y } = node.position;
          const width = node.data.width || 150; // Default node width
          const height = node.data.height || 50; // Default node height
          
          minX = Math.min(minX, x);
          maxX = Math.max(maxX, x + width);
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y + height);
        });
        
        // Get the viewport dimensions
        const viewport = reactFlowInstance.getViewport();
        const container = reactFlowWrapper.current;
        if (!container) return;
        
        const containerRect = container.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;
        
        // Calculate the scale to fit the width
        const contentWidth = maxX - minX;
        const scaleX = (containerWidth * 0.9) / contentWidth; // 90% of container width
        
        // Keep the current Y position but center horizontally
        const centerX = (minX + maxX) / 2;
        const centerY = viewport.y + (containerHeight / 2) / viewport.zoom;
        
        // Set the new viewport
        reactFlowInstance.setViewport({
          // x: minX + 100,
          y: minY + 100,
          x: containerWidth / 2 - centerX * scaleX,
          // y: containerHeight / 2 - centerY * scaleX,
          zoom: scaleX,
        });
      }, 200);
    },
  }));

  return (
    <ReactFlowProvider>
      <div
        className="h-full"
        ref={reactFlowWrapper}
        onContextMenu={!readOnly ? onCanvasContextMenu : () => false}
        onClick={() => (!readOnly ? setActiveNodeId : () => false)}
      >
        {/* Render the NodeContextMenu component */}
        <ContextMenu
          id={MENU_ID}
          items={getNodeDropdownItems(
            {
              // campaignData,
              // integrations,
              nodeId: activeNodeId,
              nodes,
              setNodes,
              setEdges,
              event: currentEventObject,
              reactFlowWrapper,
              reactFlowInstance,
            },
            hideAll
          )}
        />

        {/* Render the ReactFlow component */}
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={!readOnly ? onNodesChange : () => false}
          onEdgesChange={!readOnly ? onEdgesChange : () => false}
          onEdgesDelete={!readOnly ? onEdgesDelete : () => false}
          onNodeDragStop={!readOnly ? onNodeDragStop : () => false}
          onNodesDelete={!readOnly ? onNodesDelete : () => false}
          onNodeClick={!readOnly ? onNodeClick : () => false}
          onConnect={!readOnly ? onConnect : () => false}
          fitView
          proOptions={{ hideAttribution: true }}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={{ style: { opacity: 0.5 } }}
          onNodeDoubleClick={onNodeDoubleClick}
          onNodeContextMenu={!readOnly ? onNodeContextMenu : () => false}
          onInit={onInit}
          onDrop={!readOnly ? onDrop : () => false}
          onDragOver={!readOnly ? onDragOver : () => false}
          connectionRadius={40}
          snapToGrid={direction === "vertical" ? true : false}
          maxZoom={7}
          // panOnDrag={panOnDrag}
          // panOnDrag={false}
          // autoPanOnNodeDrag={false}
          panOnScroll={panOnScroll}
          // panOnScrollMode={PanOnScrollMode.Vertical}

        >
          {/* Render the MiniMap component */}
          {showMiniMap && (
            <MiniMap className="dark:bg-black" zoomable pannable />
          )}

          {/* Render the Controls component */}
          {showControls && <Controls />}

          {/* {direction === "vertical" && <Background variant={BackgroundVariant.Dots} />} */}
        </ReactFlow>
      </div>
    </ReactFlowProvider>
  );
});

export default PureCampaignBuilder;